/**
 * PipelineHealthCard — Iter-126
 *
 * Ageing-bucket dashboard for currently PENDING quotations. Answers
 * "how much of my pipeline is going stale?" at a glance.
 *
 * Buckets: Fresh (0-7d) / Follow-up needed (8-30d) / Cold (31-90d) /
 * Stale (>90d). Backend returns count, value, %, top 3 highest-value
 * quotes per bucket. UI shows animated stacked bar + per-bucket cards
 * with drill-through.
 *
 * Thresholds are configurable via a small popover so the owner can tune
 * "what counts as fresh vs warm" for their business rhythm.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Activity, AlertTriangle, CheckCircle2, ChevronRight,
  Flame, Snowflake, Sparkles, Settings2, Clock,
} from 'lucide-react';
import { api } from '../../lib/store';
import { logger } from '../../lib/logger';
import { toast } from 'sonner';

const short = (n) => {
  if (n === null || n === undefined || isNaN(n)) return '₹0';
  const a = Math.abs(n);
  if (a >= 10000000) return `₹${(n / 10000000).toFixed(2)}Cr`;
  if (a >= 100000) return `₹${(n / 100000).toFixed(2)}L`;
  if (a >= 1000) return `₹${(n / 1000).toFixed(0)}K`;
  return `₹${Math.round(n)}`;
};

// Bucket visual config — colors chosen to match the app's olive/gold palette
const BUCKET_STYLE = {
  fresh: {
    icon: Sparkles, color: '#2E7D32', soft: '#2E7D3216',
    grad: 'from-[#2E7D32] to-[#66BB6A]', text: 'text-[#2E7D32]',
  },
  warm: {
    icon: Clock, color: '#DAA520', soft: '#DAA52016',
    grad: 'from-[#DAA520] to-[#F1CB6B]', text: 'text-[#8B6914]',
  },
  cold: {
    icon: Snowflake, color: '#6A93B0', soft: '#6A93B016',
    grad: 'from-[#6A93B0] to-[#9EB9CE]', text: 'text-[#1F4E79]',
  },
  stale: {
    icon: AlertTriangle, color: '#C62828', soft: '#C6282816',
    grad: 'from-[#C62828] to-[#EF5350]', text: 'text-[#C62828]',
  },
};

const FLAG_STYLE = {
  good:    { icon: CheckCircle2, cls: 'bg-[#2E7D32]/10 border-[#2E7D32]/40 text-[#2E7D32]',    ring: 'ring-[#2E7D32]/20' },
  warn:    { icon: AlertTriangle, cls: 'bg-[#DAA520]/10 border-[#DAA520]/40 text-[#8B6914]',   ring: 'ring-[#DAA520]/20' },
  bad:     { icon: AlertTriangle, cls: 'bg-[#C62828]/10 border-[#C62828]/40 text-[#C62828]',   ring: 'ring-[#C62828]/20' },
  neutral: { icon: Activity, cls: 'bg-[#F3F4EE] border-[#E2E6D9] text-[#5C6150]',              ring: 'ring-[#E2E6D9]/40' },
};

// -------- count-up number ----------
function CountUp({ value, format = (n) => Math.round(n).toString(), duration = 0.7 }) {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    let raf;
    const start = performance.now();
    const from = 0, to = Number(value) || 0;
    const step = (now) => {
      const t = Math.min(1, (now - start) / (duration * 1000));
      setDisplay(from + (to - from) * (1 - Math.pow(1 - t, 3)));
      if (t < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [value, duration]);
  return <span>{format(display)}</span>;
}

// -------- stacked bar segments ----------
function StackedBar({ buckets, totalValue }) {
  if (!totalValue || totalValue <= 0) {
    return (
      <div className="h-3 w-full rounded-full bg-[#F3F4EE] border border-[#E2E6D9]" />
    );
  }
  const segments = buckets.filter(b => b.value > 0);
  return (
    <div className="w-full h-3 rounded-full bg-[#F3F4EE] border border-[#E2E6D9] overflow-hidden flex" data-testid="pipeline-stacked-bar">
      {segments.map((b, i) => {
        const style = BUCKET_STYLE[b.key] || BUCKET_STYLE.warm;
        return (
          <motion.div
            key={b.key}
            initial={{ width: 0 }}
            animate={{ width: `${b.pct}%` }}
            transition={{ duration: 0.9, delay: i * 0.12, ease: 'easeOut' }}
            className="h-full"
            style={{ background: `linear-gradient(90deg, ${style.color}, ${style.color}CC)` }}
            title={`${b.label}: ${b.pct}%`}
          />
        );
      })}
    </div>
  );
}

// -------- per-bucket tile ----------
function BucketTile({ bucket, onDrill }) {
  const style = BUCKET_STYLE[bucket.key] || BUCKET_STYLE.warm;
  const Icon = style.icon;
  const rangeLabel =
    bucket.age_to === null
      ? `> ${bucket.age_from - 1}d old`
      : bucket.age_from === 0
      ? `0–${bucket.age_to}d old`
      : `${bucket.age_from}–${bucket.age_to}d old`;
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      whileHover={{ y: -2 }}
      className="rounded-xl border border-[#E2E6D9] bg-white overflow-hidden shadow-sm hover:shadow-md transition-shadow"
      data-testid={`pipeline-bucket-${bucket.key}`}
    >
      <div className={`h-1 bg-gradient-to-r ${style.grad}`} />
      <div className="p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1.5">
            <div className="p-1 rounded" style={{ background: style.soft }}>
              <Icon className="w-3.5 h-3.5" style={{ color: style.color }} />
            </div>
            <span className={`text-[10px] uppercase tracking-wider font-semibold ${style.text}`}>{bucket.label}</span>
          </div>
          <span className="text-[9px] text-[#8F9485] tabular-nums">{rangeLabel}</span>
        </div>
        <p className="text-xl font-bold text-[#1A1C16] font-mono-data leading-tight">
          <CountUp value={bucket.value} format={short} />
        </p>
        <div className="flex items-center justify-between mt-1">
          <p className="text-[11px] text-[#5C6150]">
            <b className="font-mono-data">{bucket.count}</b> quote{bucket.count === 1 ? '' : 's'}
          </p>
          <span className="text-[10px] font-semibold text-[#5C6150] bg-[#F3F4EE] px-1.5 py-0.5 rounded-full tabular-nums">
            {bucket.pct}%
          </span>
        </div>
        {bucket.top.length > 0 && (
          <div className="mt-2 pt-2 border-t border-[#E2E6D9] space-y-1">
            {bucket.top.slice(0, 3).map((q) => (
              <button
                key={q.id}
                onClick={() => onDrill(q)}
                className="w-full flex items-center justify-between gap-1 text-left hover:bg-[#F9FAF7] px-1 py-0.5 rounded transition-colors group"
                data-testid={`pipeline-top-quote-${q.id}`}
              >
                <span className="text-[11px] text-[#1A1C16] truncate flex-1">{q.customer}</span>
                <span className="text-[10px] font-mono-data font-semibold" style={{ color: style.color }}>{short(q.value)}</span>
                <ChevronRight className="w-3 h-3 text-[#8F9485] group-hover:text-[#556B2F] flex-shrink-0" />
              </button>
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
}

// -------- threshold-tuning popover ----------
function ThresholdEditor({ thresholds, onSave, onClose }) {
  const [t, setT] = useState(thresholds);
  const save = () => {
    if (t.fresh_days < t.warm_days && t.warm_days < t.cold_days) {
      onSave(t);
    } else {
      toast.error('Fresh days must be less than Warm days, and Warm less than Cold.');
    }
  };
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.96 }}
      className="absolute right-0 top-full mt-1 w-72 z-20 rounded-lg border border-[#E2E6D9] bg-white shadow-lg p-3"
      data-testid="pipeline-threshold-editor"
    >
      <p className="text-xs font-semibold mb-2 text-[#1A1C16]">Ageing buckets — set your own thresholds</p>
      <div className="grid grid-cols-3 gap-2">
        {[
          { k: 'fresh_days', label: 'Fresh (≤)' },
          { k: 'warm_days', label: 'Warm (≤)' },
          { k: 'cold_days', label: 'Cold (≤)' },
        ].map(({ k, label }) => (
          <div key={k}>
            <Label className="text-[9px] uppercase text-[#8F9485]">{label}</Label>
            <Input
              type="number" min={1} max={365}
              value={t[k]}
              onChange={(e) => setT({ ...t, [k]: Number(e.target.value) || 0 })}
              className="h-8 text-xs"
              data-testid={`pipeline-threshold-${k}`}
            />
          </div>
        ))}
      </div>
      <p className="text-[10px] text-[#8F9485] mt-1.5">Anything older than the Cold threshold is flagged as “Stale” 🔴</p>
      <div className="flex gap-2 mt-2 justify-end">
        <Button size="sm" variant="ghost" onClick={onClose} className="h-7 text-xs">Cancel</Button>
        <Button size="sm" onClick={save} className="h-7 text-xs bg-[#556B2F] hover:bg-[#3F5222] text-white" data-testid="pipeline-threshold-save">Apply</Button>
      </div>
    </motion.div>
  );
}

// ============ Main card ============
export default function PipelineHealthCard() {
  const navigate = useNavigate();
  const [thresholds, setThresholds] = useState({ fresh_days: 7, warm_days: 30, cold_days: 90 });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editOpen, setEditOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError('');
    api.getPipelineHealth(thresholds)
      .then(r => setData(r.data))
      .catch(e => {
        logger.warn('pipeline-health fetch failed', e);
        setError(e.response?.data?.detail || 'Failed to load pipeline');
      })
      .finally(() => setLoading(false));
  }, [thresholds]);

  const flagStyle = FLAG_STYLE[data?.flag || 'neutral'];
  const FlagIcon = flagStyle.icon;

  const drillTo = (q) => {
    // Owner clicks a top-value pending quote → jump to quotation detail
    navigate(`/quotations/${q.id}`);
  };

  if (loading) {
    return (
      <Card className="border-[#E2E6D9]" data-testid="pipeline-health-loading">
        <CardContent className="pt-4">
          <Skeleton className="h-6 w-48 mb-3" />
          <Skeleton className="h-3 w-full mb-3" />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-32" />)}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error || !data) {
    return (
      <Card className="border-[#E2E6D9]" data-testid="pipeline-health-error">
        <CardContent className="pt-4">
          <p className="text-sm text-[#B93838]">{error || 'No pipeline data'}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={`border overflow-visible ${flagStyle.cls} ring-4 ${flagStyle.ring}`} data-testid="pipeline-health-card">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-2">
            <div className="p-1.5 rounded-lg bg-white/60 border border-white/40">
              <FlagIcon className="w-4 h-4" />
            </div>
            <div>
              <CardTitle className="text-sm font-bold uppercase tracking-wide flex items-center gap-2">
                <Activity className="w-4 h-4" /> Pending Pipeline Health
              </CardTitle>
              <p className="text-xs mt-0.5 font-medium">{data.headline}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <p className="text-[10px] uppercase tracking-wider opacity-70">Total pending</p>
              <p className="text-lg font-bold font-mono-data leading-tight">
                <CountUp value={data.total.value} format={short} /> · <span className="text-xs opacity-80">{data.total.count} quotes</span>
              </p>
            </div>
            <div className="relative">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setEditOpen(o => !o)}
                className="h-8 w-8 p-0"
                title="Tune bucket thresholds"
                data-testid="pipeline-threshold-toggle"
              >
                <Settings2 className="w-4 h-4" />
              </Button>
              <AnimatePresence>
                {editOpen && (
                  <ThresholdEditor
                    thresholds={thresholds}
                    onSave={(t) => { setThresholds(t); setEditOpen(false); }}
                    onClose={() => setEditOpen(false)}
                  />
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-1 space-y-3">
        {/* Stacked bar showing value split across buckets */}
        <StackedBar buckets={data.buckets} totalValue={data.total.value} />
        <div className="flex flex-wrap gap-x-3 gap-y-1 text-[10px] font-medium">
          {data.buckets.map(b => {
            const s = BUCKET_STYLE[b.key];
            return (
              <span key={b.key} className={`inline-flex items-center gap-1 ${s.text}`}>
                <span className="inline-block w-2 h-2 rounded-full" style={{ background: s.color }} />
                {b.label} · {b.pct}%
              </span>
            );
          })}
        </div>

        {/* Per-bucket tiles with top-3 drill-through */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {data.buckets.map(b => (
            <BucketTile key={b.key} bucket={b} onDrill={drillTo} />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
